import { NextPage } from "next";

import ImagineContainer from "@/layout/ImagineContainer/ImagineContainer";
import ImageGallery from "@/components/ImageGallery/ImageGallery";
import MinecraftSkin from "@/components/MinecraftSkin/MinecraftSkin";
import MinecraftSkinEditor from "@/components/MinecraftSkinEditor/MinecraftSkinEditor";
import SkinGeneratorMenu from "@/components/SkinGeneratorMenu/SkinGeneratorMenu";

import EmailVerificationModal from "@/components/EmailVerificationModal/EmailVerificationModal";
import { GalleryIcon } from "@/components/icons/gallery";
import { useDiffusion } from "@/context/SDContext";
import { useImagine } from "@/context/ImagineContext";
import { useAnalytics } from "@/context/GoogleAnalyticsContext";
import styles from "@/styles/pages/Generator.module.scss";
import { useEffect, useState, useCallback } from "react";
import { ArtWork, Preview, Prompt } from "@/models/models";
import {
  getUserArtWorks,
  saveArtWork,
  deleteArtWork,
} from "@/services/artworks";

import { useToastContext } from "@/context/ToastContext";
import { getUserCollections } from "@/services/collection";
import { useCredits } from "@/context/CreditsContext";
import SkinPreviews from "@/components/SkinPreviews/SkinPreviews";
import GeneratingOverlay from "@/components/GeneratingOverlay/GeneratingOverlay";



const Generator: NextPage = () => {
  const [showEditor, setShowEditor] = useState(false);

  const handleEdit = () => {
    setShowEditor(true);
  };

  const handleEditorSave = (dataUrl: string) => {
    setSkinUrl(dataUrl);
    setShowEditor(false);
  };

  const handleEditorCancel = () => {
    setShowEditor(false);
  };

  const { prompt, setPrompt } = useDiffusion();
  const { sendAnalyticsRecord } = useAnalytics();
  const { preview, approveImage, resultImages, generateImages, generateImagesCallbackStore, isLoading, isApproving } =
    useImagine();
  const { loadWallets } = useCredits();
  const isFormValid = prompt.value.length > 0;

  const [toggleAnimation, setToggleAnimation] = useState(true);

  const [skinUrl, setSkinUrl] = useState<string>();
  const [skinPrompt, setSkinPrompt] = useState<Prompt>();
  const [artworkId, setArtworkId] = useState<string>("");
  const [selectedCollectionId, setSelectedCollectionId] = useState<string>("");
  const [retryCount, setRetryCount] = useState(0);
  const [artworks, setArtworks] = useState<ArtWork[]>([]);
  const [showMobileGallery, setShowMobileGallery] = useState<boolean>(false);
  const [selectedPreviewIndex, setSelectedPreviewIndex] = useState<number | null>(null);



  const { showSuccessAlert, showWarningAlert, showErrorAlert } =
    useToastContext();

  const loadUserArtworks = useCallback(async () => {
    const response = await getUserArtWorks();
    if (response.success && response.data) {
      const artworksData = response.data || [];
      loadWallets();
      setArtworks(artworksData);
    }
  }, [loadWallets]);

  const handleSubmit = useCallback(
    (images: any) => {
      const handleSaveArtWork = async (artwork: ArtWork) => {
        const saveFn = saveArtWork;
        saveFn(artwork)
          .then((response) => {
            if (response && response.success) {
              setArtworkId(response.data.id);
              loadUserArtworks();
              loadWallets();
              setSkinUrl(artwork.image);
              setSkinPrompt(artwork.prompt);
            }
          })
          .catch((error) => {
            console.error(error);
            showErrorAlert("Error saving artwork, please try again");
          });
      };

      // TODO: Refactor to not rely on indices
      const preview = images[0]["images"][1];
      const newSkinUrl = images[0]["images"][0];
      const newSkinPrompt = images[0].prompt;
      handleSaveArtWork({
        image: newSkinUrl,
        preview: preview,
        prompt: newSkinPrompt,
        title: "minecraft skin",
        collection_id: selectedCollectionId,
      });
    },
    [showErrorAlert, selectedCollectionId, loadUserArtworks, loadWallets]
  );

  useEffect(() => {
    const myCallback = (latestResult: any) => {
      handleSubmit(latestResult);
    };

    generateImagesCallbackStore.add(myCallback);
    return () => generateImagesCallbackStore.remove(myCallback);
  }, [handleSubmit, generateImagesCallbackStore]);

  useEffect(() => {
    loadUserArtworks();
  }, [resultImages, loadUserArtworks]);

  useEffect(() => {
    const _artwork: ArtWork = artworks[0];
    if (!_artwork) return;

    // Only auto-select the first artwork if we are currently viewing the default 'notch.png'
    // This prevents overwriting a newly generated/selected skin when the gallery refreshes.
    const isDefault = !skinUrl || skinUrl.includes("notch.png");

    if (isDefault) {
      setSkinUrl(_artwork.image);
      setSkinPrompt(_artwork.prompt);
      if (_artwork.id) setArtworkId(_artwork.id);
    }
  }, [artworks, skinUrl]);

  useEffect(() => {
    const url = new URL("./notch.png", window.location.href);
    if (skinUrl === undefined) {
      setSkinUrl(url.href);
    }
  }, [skinUrl]);

  useEffect(() => {
    const fetchCollections = async () => {
      const response = await getUserCollections();
      if (response.success && response.data) {
        const collectionsData = response.data || [];
        setSelectedCollectionId(collectionsData[0].id);
      } else {
        if (retryCount < 10) {
          setTimeout(() => {
            setRetryCount(retryCount + 1);
          }, 500);
        }
      }
    };
    fetchCollections();
  }, [retryCount]);

  const handleGenerate = () => {
    generateImages({
      option: "minecraft",
      prompt: prompt.value,
    });
    sendAnalyticsRecord("generate_images", {
      prompt: prompt.value,
      model: "minecraft",
    });
  };

  const handleDeleteArtwork = async () => {
    if (!artworkId) {
      showWarningAlert(
        "Looks like the item you're attempting to delete is not a Skin."
      );
      return;
    }
    showWarningAlert(
      "Are you sure you want to delete this Skin?",
      "Confirm",
      deleteCallback
    );
  };

  const deleteCallback = () => {
    if (!artworkId) return;
    deleteArtWork(artworkId)
      .then((response) => {
        if (response && response.success) {
          const remaining = artworks.filter((a) => a.id !== artworkId);
          const next = remaining[0];
          if (next) {
            setSkinUrl(next.image);
            setSkinPrompt(next.prompt);
            setArtworkId(next.id || "");
          } else {
            setSkinUrl(undefined);
            setSkinPrompt(undefined);
            setArtworkId("");
          }
          loadUserArtworks();
          showSuccessAlert("Skin deleted successfully");
        }
      })
      .catch(() => {
        showErrorAlert(
          "An error occurred while deleting Skin, please try again later"
        );
      });
  };

  const switchSkinRenderer = (artWork: ArtWork) => {
    setSkinUrl(artWork.image);
    setSkinPrompt(artWork.prompt);
    setArtworkId(artWork.id || "");
    setShowMobileGallery(false);
    if (artWork.prompt?.prompt) {
      setPrompt({ value: artWork.prompt?.prompt });
    }
  };

  const handleSelectPreview = (preview: Preview, approvedImageIndex: number) => {
    setSelectedPreviewIndex(approvedImageIndex);
    approveImage(preview.request, preview.generationId, approvedImageIndex);
  }


  useEffect(() => {
    if (!preview) setSelectedPreviewIndex(null);
  }, [preview]);

  const activePreview = preview;

  return (
    <ImagineContainer formValid={isFormValid} handleGenerate={handleGenerate}>
      <GeneratingOverlay isVisible={isLoading && !preview} estimatedSeconds={30} />
      <div className={styles.imagesContent}>
        <div className={`${styles.results} ${styles.fullWidth} ${showMobileGallery && styles.visible}`}>
          <ImageGallery
            onClick={switchSkinRenderer}
            ArtWorks={artworks}
          />
        </div>
        {activePreview && (
          <SkinPreviews
            preview={activePreview}
            handleSelectPreview={handleSelectPreview}
            isApproving={isApproving}
            selectedIndex={selectedPreviewIndex}
          />
        )}
        {!activePreview && skinUrl && (
          <MinecraftSkin
            url={skinUrl}
            toggleAnimation={toggleAnimation}
          />
        )}
        {!activePreview && (
          <div className={styles.galleryToggleWrapper}>
            <button
              className={styles.galleryToggle}
              onClick={() => setShowMobileGallery(!showMobileGallery)}
            >
              {showMobileGallery ? <div className={styles.squareIcon}></div> : <GalleryIcon />}
            </button>
          </div>
        )}
      </div>

      {showEditor && skinUrl && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', zIndex: 1000, background: '#1a1b1e' }}>
          <MinecraftSkinEditor
            skinUrl={skinUrl}
            onSave={handleEditorSave}
            onCancel={handleEditorCancel}
          />
        </div>
      )}

      <SkinGeneratorMenu
        artwork={{
          id: artworkId,
          image: skinUrl || "",
          prompt: skinPrompt,
        }}
        deleteArtwork={handleDeleteArtwork}
        toggleAnimation={toggleAnimation}
        setToggleAnimation={setToggleAnimation}
        onEdit={handleEdit}
      />
      <EmailVerificationModal />
    </ImagineContainer>
  );
};

export default Generator;
